/* Upload files first, then submit links through Shopify's native contact form.
   Never post file bytes to /contact: that endpoint is not a file-storage API. */
if (!customElements.get('mdiy-inquiry')) {
  customElements.define('mdiy-inquiry', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.abort = new AbortController();
      this.files = []; this.busy = false;
      this.form = this.querySelector('form');
      this.input = this.querySelector('[data-files]');
      this.error = this.querySelector('[data-upload-error]');
      this.status = this.querySelector('[data-upload-status]');
      this.button = this.querySelector('[data-submit]');
      this.label = this.querySelector('[data-submit-label]');
      this.originalLabel = this.label.textContent;
      const result = this.querySelector('.mdiy-inquiry__success, .mdiy-inquiry__error[tabindex]');
      if (result) result.focus();
      if (!this.input) return;
      try {
        const url = new URL(this.dataset.cdn.trim());
        if (url.protocol !== 'https:' || url.username || url.password || url.search || url.hash || url.pathname !== '/') throw new Error();
        this.cdn = url.origin;
      } catch { return; }
      const options = { signal: this.abort.signal };
      this.input.disabled = false;
      this.querySelector('[data-upload-area]').hidden = false;
      this.querySelector('[data-upload-unavailable]').hidden = true;
      const setup = this.querySelector('[data-upload-setup]');
      if (setup) setup.hidden = true;
      this.list = this.querySelector('[data-file-list]');
      this.links = this.querySelector('[data-attachment-links]');
      this.input.addEventListener('change', () => { this.addFiles([...this.input.files]); this.input.value = ''; }, options);
      const drop = this.querySelector('[data-drop]');
      for (const event of ['dragenter', 'dragover']) drop.addEventListener(event, e => { e.preventDefault(); if (!this.busy) drop.classList.add('is-dragging'); }, options);
      for (const event of ['dragleave', 'drop']) drop.addEventListener(event, e => { e.preventDefault(); drop.classList.remove('is-dragging'); }, options);
      drop.addEventListener('drop', e => { if (!this.busy) this.addFiles([...e.dataTransfer.files]); }, options);
      this.form.addEventListener('submit', e => this.submit(e), { ...options, capture: true });
      this.form.addEventListener('reset', () => { if (!this.busy) { this.files = []; this.renderFiles(); } }, options);
    }
    showError(message) { this.error.textContent = message; this.error.hidden = !message; }
    addFiles(incoming) {
      if (this.busy) return;
      this.showError(''); this.status.textContent = '';
      const accepted = [];
      for (const file of incoming) {
        // Browsers report inconsistent MIME types for AI, PSD and HEIC files.
        if (!/\.(jpe?g|png|webp|heic|heif|tiff?|pdf|ai|psd|zip)$/i.test(file.name)) {
          this.showError('Please choose JPG, PNG, WebP, HEIC, TIFF, PDF, AI, PSD or ZIP files.'); return;
        }
        if (!file.size || file.size > 20 * 1024 * 1024) { this.showError('Each file must be between 1 byte and 20 MB. Please choose a smaller file.'); return; }
        const id = `${file.name}:${file.size}:${file.lastModified}`;
        if (!this.files.some(item => item.id === id) && !accepted.some(item => item.id === id)) accepted.push({ id, file, url: '' });
      }
      if (this.files.length + accepted.length > 5) { this.showError('You can attach up to 5 files. Remove a file before adding more.'); return; }
      this.files.push(...accepted); this.renderFiles();
    }
    renderFiles() {
      this.list.replaceChildren();
      this.files.forEach(item => {
        const li = document.createElement('li'), text = document.createElement('span'), button = document.createElement('button');
        text.textContent = `${item.file.name} · ${(item.file.size / 1024 / 1024).toFixed(1)} MB${item.url ? ' · Uploaded' : ''}`;
        button.type = 'button'; button.textContent = 'Remove'; button.disabled = this.busy;
        button.setAttribute('aria-label', `Remove ${item.file.name}`);
        button.addEventListener('click', () => {
          if (this.busy) return;
          this.files = this.files.filter(other => other !== item); this.renderFiles(); this.showError(''); this.status.textContent = ''; this.input.focus();
        }, { signal: this.abort.signal });
        li.append(text, button); this.list.append(li);
      });
      this.links.value = this.files.filter(item => item.url).map(item => `${item.file.name.replace(/[\r\n]/g, ' ')}: ${item.url}`).join('\n');
    }
    setBusy(busy) {
      this.busy = busy; this.button.disabled = busy; this.input.disabled = busy;
      this.form.setAttribute('aria-busy', String(busy));
      this.label.textContent = busy ? 'Uploading files…' : this.originalLabel;
      this.renderFiles();
    }
    uploadFile(body, index) {
      return new Promise((resolve, reject) => {
        const xhr = this.uploadRequest = new XMLHttpRequest();
        xhr.open('POST', 'https://upload.uploadcare.com/base/');
        xhr.responseType = 'json';
        xhr.timeout = 300000;
        xhr.upload.onprogress = event => {
          if (!this.isConnected) return;
          const percent = event.lengthComputable ? ` ${Math.min(100, Math.round(event.loaded / event.total * 100))}%` : '';
          this.status.textContent = `Uploading file ${index + 1} of ${this.files.length}${percent}…`;
        };
        xhr.onload = () => xhr.status >= 200 && xhr.status < 300 && xhr.response ? resolve(xhr.response) : reject(new Error('upload'));
        xhr.onerror = xhr.ontimeout = xhr.onabort = () => reject(new Error('upload'));
        xhr.send(body);
      });
    }
    async submit(event) {
      if (!this.busy && this.files.every(item => item.url)) return;
      event.preventDefault(); event.stopImmediatePropagation();
      if (this.busy || !this.form.reportValidity()) return;
      this.showError(''); this.setBusy(true);
      const submitter = event.submitter || this.button;
      try {
        for (let i = 0; i < this.files.length; i++) {
          const item = this.files[i];
          if (item.url) continue;
          this.status.textContent = `Uploading file ${i + 1} of ${this.files.length}…`;
          const body = new FormData();
          body.append('UPLOADCARE_PUB_KEY', this.dataset.publicKey);
          body.append('UPLOADCARE_STORE', '1');
          body.append('file', item.file);
          const data = await this.uploadFile(body, i);
          if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(data.file || '')) throw new Error('response');
          item.url = `${this.cdn}/${data.file}/`;
          this.renderFiles();
        }
        if (!this.isConnected) return;
        this.status.textContent = 'Files uploaded. Sending your inquiry…';
        this.setBusy(false);
        // requestSubmit preserves native validation and Shopify CAPTCHA handlers.
        this.form.requestSubmit(submitter);
      } catch {
        if (!this.isConnected) return;
        this.status.textContent = '';
        this.showError('Your files could not be uploaded. Please try sending again, or remove the files and share a reference link. Your inquiry has not been sent.');
        this.setBusy(false);
      }
    }
    disconnectedCallback() { this.abort?.abort(); this.uploadRequest?.abort(); this.initialized = false; }
  });
}
